import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pin, Megaphone, Calendar, Shirt, GraduationCap, Sparkles, MessageCircle, CalendarDays, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { getEventTypeMeta, formatEventDate, formatEventRange } from "@/lib/events";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => setAnnouncements([])),
      api.get("/events").then((r) => setEvents(r.data.slice(0, 4))).catch(() => setEvents([])),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="mx-auto max-w-7xl w-full px-6 py-12 flex-1">
        <div className="animate-fade-up">
          <div className="overline mb-3">Parent Dashboard</div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">
            Hey, {user?.name?.split(" ")[0] || "Parent"} <span className="text-diamond">✦</span>
          </h1>
          <p className="mt-3 text-white/65 max-w-xl">
            {user?.dancer_name
              ? <>Latest updates for <span className="text-white">{user.dancer_name}</span> and the squad.</>
              : <>Latest updates for your dancer and the squad.</>
            }
          </p>
        </div>

        {/* Quick actions */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-quick-actions">
          <Link to="/messages" className="card-dark p-5 flex items-center gap-3" data-testid="quick-messages">
            <div className="h-10 w-10 rounded-md grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF]">
              <MessageCircle size={18} />
            </div>
            <div>
              <div className="font-varsity tracking-wider">Message Coach</div>
              <div className="text-xs text-white/50">Direct 1-on-1 chat</div>
            </div>
          </Link>
          <Link to="/tuition" className="card-dark p-5 flex items-center gap-3" data-testid="quick-tuition">
            <div className="h-10 w-10 rounded-md grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF]">
              <GraduationCap size={18} />
            </div>
            <div>
              <div className="font-varsity tracking-wider">Tuition Plans</div>
              <div className="text-xs text-white/50">View pricing & plans</div>
            </div>
          </Link>
          <Link to="/shop" className="card-dark p-5 flex items-center gap-3" data-testid="quick-shop">
            <div className="h-10 w-10 rounded-md grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF]">
              <Shirt size={18} />
            </div>
            <div>
              <div className="font-varsity tracking-wider">Team Shop</div>
              <div className="text-xs text-white/50">Shirts & uniforms</div>
            </div>
          </Link>
          <Link to="/events" className="card-dark p-5 flex items-center gap-3" data-testid="quick-events">
            <div className="h-10 w-10 rounded-md grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF]">
              <Calendar size={18} />
            </div>
            <div>
              <div className="font-varsity tracking-wider">Schedule</div>
              <div className="text-xs text-white/50">Practices &amp; performances</div>
            </div>
          </Link>
        </div>

        {/* Upcoming events strip */}
        {events.length > 0 && (
          <section className="mt-12" data-testid="dashboard-events-section">
            <div className="flex items-end justify-between mb-5">
              <div className="flex items-center gap-2">
                <CalendarDays className="text-[#00E5FF]" size={18} />
                <h2 className="font-varsity text-2xl tracking-wider">Upcoming Events</h2>
              </div>
              <Link to="/events" className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#00E5FF]">
                Full schedule →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {events.map((ev) => {
                const meta = getEventTypeMeta(ev.event_type);
                const d = formatEventDate(ev.starts_at, ev.all_day);
                return (
                  <article key={ev.id} className="card-dark p-4 flex gap-4" data-testid={`dashboard-event-${ev.id}`}>
                    <div className="shrink-0 w-14 text-center rounded-lg border border-white/10 bg-white/5 py-2 self-start">
                      <div className="text-[9px] uppercase tracking-[0.25em] text-[#00E5FF]">{d.month}</div>
                      <div className="font-display text-2xl text-chrome leading-none mt-1">{d.day}</div>
                    </div>
                    <div className="min-w-0">
                      <span className={`inline-block text-[10px] uppercase tracking-[0.18em] border px-2 py-0.5 rounded-full ${meta.accent}`}>{meta.label}</span>
                      <div className="font-varsity text-base tracking-wider mt-2 truncate">{ev.title}</div>
                      <div className="text-xs text-white/55 mt-1 flex items-center gap-1.5">
                        <CalendarDays size={11} /> {formatEventRange(ev.starts_at, ev.ends_at, ev.all_day)}
                      </div>
                      {ev.location && (
                        <div className="text-xs text-white/45 mt-1 flex items-center gap-1.5 truncate">
                          <MapPin size={11} /> {ev.location}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* Announcements */}
        <section className="mt-12" data-testid="announcements-section">
          <div className="flex items-center gap-2 mb-5">
            <Megaphone className="text-[#00E5FF]" size={18} />
            <h2 className="font-varsity text-2xl tracking-wider">Weekly Announcements</h2>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="card-dark p-5 h-24 animate-pulse" />
              ))}
            </div>
          ) : announcements.length === 0 ? (
            <div className="card-dark p-8 text-center text-white/60" data-testid="announcements-empty">
              <Sparkles className="mx-auto mb-3 text-[#00E5FF]" />
              No announcements yet — check back soon!
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((a) => (
                <article key={a.id} className="card-dark p-6" data-testid={`announcement-${a.id}`}>
                  <div className="flex items-start gap-3">
                    {a.pinned && (
                      <div className="mt-1 text-[#00E5FF]" title="Pinned">
                        <Pin size={14} />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="font-varsity text-xl tracking-wider">{a.title}</h3>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 shrink-0">
                          {formatDate(a.created_at)}
                        </span>
                      </div>
                      <p className="mt-2 text-white/70 leading-relaxed whitespace-pre-wrap">{a.body}</p>
                      {a.author_name && (
                        <div className="mt-3 text-xs text-white/40 tracking-wider uppercase">
                          — {a.author_name}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
